import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { parseISO, isValid as isValidDate } from 'date-fns';
import { Form, Col, Card, Alert } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAPlantio } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import CoordenadaDmsField from './components/CoordenadaDmsField';
import { formatCoordenadaDmsMask } from '../../util/cadernoLPMA/coordenadaDmsMask';

const COORDENADAS_PLANTIO_ROWS: Array<Array<{ name: keyof CadernoLPMAPlantio; label: string }>> = [
	[
		{ name: 'coordenadaP01', label: 'P01' },
		{ name: 'coordenadaP02', label: 'P02' },
	],
	[
		{ name: 'coordenadaP03', label: 'P03' },
		{ name: 'coordenadaP04', label: 'P04' },
	],
	[
		{ name: 'coordenadaP05', label: 'P05' },
		{ name: 'coordenadaP06', label: 'P06' },
	],
	[
		{ name: 'coordenadaP07', label: 'P07' },
		{ name: 'coordenadaP08', label: 'P08' },
	],
	[
		{ name: 'coordenadaP09', label: 'P09' },
		{ name: 'coordenadaP10', label: 'P10' },
	],
	[
		{ name: 'coordenadaP11', label: 'P11' },
		{ name: 'coordenadaP12', label: 'P12' },
	],
];

const COORDENADAS_FCN30_ROWS: Array<Array<{ name: keyof CadernoLPMAPlantio; label: string }>> = [
	[
		{ name: 'fcn30CoordenadaP01', label: 'P01' },
		{ name: 'fcn30CoordenadaP02', label: 'P02' },
	],
	[
		{ name: 'fcn30CoordenadaP03', label: 'P03' },
		{ name: 'fcn30CoordenadaP04', label: 'P04' },
	],
	[
		{ name: 'fcn30CoordenadaP05', label: 'P05' },
		{ name: 'fcn30CoordenadaP06', label: 'P06' },
	],
	[
		{ name: 'fcn30CoordenadaP07', label: 'P07' },
		{ name: 'fcn30CoordenadaP08', label: 'P08' },
	],
	[
		{ name: 'fcn30CoordenadaP09', label: 'P09' },
		{ name: 'fcn30CoordenadaP10', label: 'P10' },
	],
	[
		{ name: 'fcn30CoordenadaP11', label: 'P11' },
		{ name: 'fcn30CoordenadaP12', label: 'P12' },
	],
];

interface PlantioModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAPlantio) => void;
	initialData?: CadernoLPMAPlantio;
}

// Helper function para acessar propriedades dinâmicas sem erros de type
const getNestedValue = (obj: any, key: string): any => obj?.[key];

const parseDateValue = (valor: any): Date | null => {
	if (!valor && valor !== 0) {
		return null;
	}

	if (valor instanceof Date) {
		return isValidDate(valor) ? valor : null;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const parsedIso = parseISO(texto);
		if (isValidDate(parsedIso)) {
			return parsedIso;
		}

		const parsedFallback = new Date(texto);
		return Number.isNaN(parsedFallback.getTime()) ? null : parsedFallback;
	}

	if (typeof valor === 'number') {
		const data = new Date(valor);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	return null;
};

const parseJsonArray = (valor: any): Array<Record<string, any>> => {
	if (!valor) {
		return [];
	}

	if (Array.isArray(valor)) {
		return valor as Array<Record<string, any>>;
	}

	if (typeof valor === 'string') {
		try {
			const parsed = JSON.parse(valor);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	if (typeof valor === 'object') {
		return [valor as Record<string, any>];
	}

	return [];
};

const camposRadioMilho = [
	'opcao1VariedadeBordaduraGM',
	'opcao2VariedadeBordaduraGM',
	'opcao3VariedadeBordaduraGM',
	'opcao4VariedadeBordaduraGM',
	'fcnOpcao1VariedadeBordaduraGM',
	'fcnOpcao2VariedadeBordaduraGM',
	'fcnOpcao3VariedadeBordaduraGM',
	'fcnOpcao4VariedadeBordaduraGM',
];

const deduzirOpcaoIsolamento = (dados?: CadernoLPMAPlantio | null): 1 | 2 | 3 | 4 => {
	if (!dados) {
		return 1;
	}

	if (
		dados.opcao4IsolamentoCrioulas ||
		dados.opcao4DistanciaMetros ||
		dados.opcao4BordaduraLinhas ||
		dados.opcao4BordaduraMetros ||
		dados.opcao4VariedadeBordadura
	) {
		return 4;
	}

	if (
		dados.opcao3BordaduraLinhas ||
		dados.opcao3BordaduraMetros ||
		dados.opcao3VariedadeBordadura ||
		dados.opcao3VariedadeBordaduraGM
	) {
		return 3;
	}

	if (
		dados.opcao2DataPlantioLPMA ||
		dados.opcao2DataFlorescimentoLPMA ||
		dados.opcao2DataPlantioAdjacencias ||
		dados.opcao2DataFlorescimentoAdjacencias ||
		dados.opcao2VariedadeBordadura ||
		dados.opcao2VariedadeBordaduraGM
	) {
		return 2;
	}

	return 1;
};

const deduzirOpcaoIsolamentoFCN = (dados?: CadernoLPMAPlantio | null): 1 | 2 | 3 | 4 => {
	if (!dados) {
		return 1;
	}

	if (
		dados.fcnOpcao4DistanciaMetros ||
		dados.fcnOpcao4BordaduraLinhas ||
		dados.fcnOpcao4BordaduraMetros ||
		dados.fcnOpcao4VariedadeBordadura ||
		dados.fcnOpcao4VariedadeBordaduraGM
	) {
		return 4;
	}

	if (
		dados.fcnOpcao3BordaduraLinhas ||
		dados.fcnOpcao3BordaduraMetros ||
		dados.fcnOpcao3VariedadeBordadura ||
		dados.fcnOpcao3VariedadeBordaduraGM
	) {
		return 3;
	}

	if (
		dados.fcnOpcao2DataPlantioLPMA ||
		dados.fcnOpcao2DataFlorescimentoLPMA ||
		dados.fcnOpcao2DataPlantioAdjacencias ||
		dados.fcnOpcao2DataFlorescimentoAdjacencias ||
		dados.fcnOpcao2VariedadeBordadura ||
		dados.fcnOpcao2VariedadeBordaduraGM
	) {
		return 2;
	}

	return 1;
};

const parseTipoPlantioString = (valor: any): { mecanico: boolean; manual: boolean } => {
	if (valor === undefined || valor === null) return { mecanico: false, manual: false };
	const s = String(valor).trim();
	if (!s) return { mecanico: false, manual: false };
	const lower = s.toLowerCase();
	const mecanico = lower.includes('mecanico');
	const manual = lower.includes('manual');
	return { mecanico, manual };
};

const tipoPlantioBooleansToStr = (mecanico: boolean, manual: boolean): string => {
	if (!mecanico && !manual) return '';
	if (mecanico && manual) return 'Mecanico,Manual';
	return mecanico ? 'Mecanico' : 'Manual';
};

const transformarPlantioMilhoParaFormulario = (dados?: CadernoLPMAPlantio | null) => {
	const valores: Record<string, any> = {};

	if (dados) {
		Object.entries(dados).forEach(([chave, valor]) => {
			if (valor !== undefined) {
				valores[chave] = valor;
			}
		});
	}

	Object.keys(valores).forEach((chave) => {
		if (chave.toLowerCase().includes('data')) {
			valores[chave] = parseDateValue(dados ? (dados as any)[chave] : undefined);
		}
	});

	// Tipo de plantio APROVADO/REALIZADO: string -> checkboxes (igual Soja/Algodão)
	const aprovado = parseTipoPlantioString(dados?.tipoPlantioAprovado);
	valores.tipoPlantioAprovadoMecanico = aprovado.mecanico;
	valores.tipoPlantioAprovadoManual = aprovado.manual;
	const realizado = parseTipoPlantioString(dados?.tipoPlantioRealizado);
	valores.tipoPlantioRealizadoMecanico = realizado.mecanico;
	valores.tipoPlantioRealizadoManual = realizado.manual;
	const fcn30Aprovado = parseTipoPlantioString(dados?.fcn30TipoPlantioAprovado);
	valores.fcn30TipoPlantioAprovadoMecanico = fcn30Aprovado.mecanico;
	valores.fcn30TipoPlantioAprovadoManual = fcn30Aprovado.manual;
	const fcn30Realizado = parseTipoPlantioString(dados?.fcn30TipoPlantioRealizado);
	valores.fcn30TipoPlantioRealizadoMecanico = fcn30Realizado.mecanico;
	valores.fcn30TipoPlantioRealizadoManual = fcn30Realizado.manual;

	camposRadioMilho.forEach((campo) => {
		if (dados && (dados as any)[campo] !== undefined && (dados as any)[campo] !== null) {
			const valor = (dados as any)[campo];
			valores[campo] = typeof valor === 'boolean' ? (valor ? 'true' : 'false') : valor;
		}
	});

	const limpeza = parseJsonArray(dados?.limpezaMaquinariosJson);
	limpeza.forEach((linha, indice) => {
		valores[`limpezaMaquinarioNome${indice}`] = linha?.nome ?? '';
		valores[`limpezaMaquinarioDataEntrada${indice}`] = parseDateValue(linha?.dataEntrada);
		valores[`limpezaMaquinarioDataSaida${indice}`] = parseDateValue(linha?.dataSaida);
		valores[`limpezaMaquinarioMetodo${indice}`] = linha?.metodo ?? '';
		valores[`limpezaMaquinarioResponsavel${indice}`] = linha?.responsavel ?? '';
	});

	const limpezaFcn30 = parseJsonArray(dados?.fcn30LimpezaMaquinariosJson);
	limpezaFcn30.forEach((linha, indice) => {
		valores[`fcn30LimpezaMaquinarioNome${indice}`] = linha?.nome ?? '';
		valores[`fcn30LimpezaMaquinarioDataEntrada${indice}`] = parseDateValue(linha?.dataEntrada);
		valores[`fcn30LimpezaMaquinarioDataSaida${indice}`] = parseDateValue(linha?.dataSaida);
		valores[`fcn30LimpezaMaquinarioMetodo${indice}`] = linha?.metodo ?? '';
		valores[`fcn30LimpezaMaquinarioResponsavel${indice}`] = linha?.responsavel ?? '';
	});

	return {
		valores,
		quantidadeLinhasLimpeza: Math.max(limpeza.length, 1),
		quantidadeLinhasLimpezaFcn30: Math.max(limpezaFcn30.length, 1),
		opcaoIsolamento: deduzirOpcaoIsolamento(dados),
		opcaoIsolamentoFcn: deduzirOpcaoIsolamentoFCN(dados),
	};
};

export default function PlantioModal({ show, onHide, onSubmit, initialData }: PlantioModalProps) {
	const {
		register: registerPlantio,
		handleSubmit: handleSubmitPlantio,
		control: controlPlantio,
		formState: { errors: errorsPlantio },
		watch: watchPlantio,
		reset: resetPlantio,
		getValues,
		setValue,
	} = useForm<CadernoLPMAPlantio>({
		defaultValues: {
			dataInicioPlantio: null,
			dataTerminoPlantio: null,
			talhaoPlantio: '',
			tipoPlantioMecanico: false,
			tipoPlantioManual: false,
			tipoPlantioAprovadoMecanico: false,
			tipoPlantioAprovadoManual: false,
			tipoPlantioRealizadoMecanico: false,
			tipoPlantioRealizadoManual: false,
			fcn30TipoPlantioAprovadoMecanico: false,
			fcn30TipoPlantioAprovadoManual: false,
			fcn30TipoPlantioRealizadoMecanico: false,
			fcn30TipoPlantioRealizadoManual: false,
			coordenadaP01: undefined,
			coordenadaP02: undefined,
			coordenadaP03: undefined,
			coordenadaP04: undefined,
			coordenadaP05: undefined,
			coordenadaP06: undefined,
			coordenadaP07: undefined,
			coordenadaP08: undefined,
			coordenadaP09: undefined,
			coordenadaP10: undefined,
			coordenadaP11: undefined,
			coordenadaP12: undefined,
			opcao1DistanciaMetros: undefined,
			opcao1BordaduraLinhas: undefined,
			opcao1BordaduraMetros: undefined,
			opcao1VariedadeBordadura: '',
			opcao1VariedadeBordaduraGM: false,
			dataComunicacaoInicioPlantio: null,
			dataComunicacaoTerminoPlantio: null,
			colaboradorResponsavelPlantio: '',
			dataRegistroPlantio: null,
			assinaturaColaboradoresBase64: null,
			dataInicioColaboradores: null,
			dataFimColaboradores: null,
			checklistPriorPlantio1CurtoPrazo: null,
			checklistPriorPlantio1MilhoCrioulas: null,
			checklistPriorPlantio1MilhoNaoGM: null,
			checklistPriorPlantio1DistanciaMilhoCrioulas: undefined,
			checklistPriorPlantio1DataPlantioMilhoCrioulas: null,
			checklistPriorPlantio1DistanciaMilhoNaoGM: undefined,
			checklistPriorPlantio1DataPlantioMilhoNaoGM: null,
			checklistPrePlantio2CurtoPrazo: false,
			checklistPrePlantio2MilhoCrioulas: false,
			checklistPrePlantio2MilhoNaoGM: false,
			checklistPrePlantio2DistanciaMilhoCrioulas: undefined,
			checklistPrePlantio2DataPlantioMilhoCrioulas: null,
			checklistPrePlantio2DistanciaMilhoNaoGM: undefined,
			checklistPrePlantio2DataPlantioMilhoNaoGM: null,
			placaBiossegurancaPreenchia: false,
			observacaoPlacaBiossegurancaPreenchia: '',
			comunicadoPlantio: false,
			observacaoComunicadoPlantio: '',
			comunicadoPlantioComCroqui: false,
			observacaoComunicadoPlantioComCroqui: '',
			envelopesDestruidos: false,
			observacaoEnvelopesDestruidos: '',
			roupasEquipamentosLimpos: false,
			observacaoRoupasEquipamentosLimpos: '',
			checklistPlantio2PlacaBiossegurancaPreenchia: false,
			observacaoChecklistPlantio2PlacaBiossegurancaPreenchia: '',
			checklistPlantio2ComunicadoPlantio: false,
			observacaoChecklistPlantio2ComunicadoPlantio: '',
			checklistPlantio2ComunicadoPlantioComCroqui: false,
			observacaoChecklistPlantio2ComunicadoPlantioComCroqui: '',
			checklistPlantio2EnvelopesDestruidos: false,
			observacaoChecklistPlantio2EnvelopesDestruidos: '',
			checklistPlantio2RoupasEquipamentosLimpos: false,
			observacaoChecklistPlantio2RoupasEquipamentosLimpos: '',
			opcao2DataPlantioLPMA: null,
			opcao2DataFlorescimentoLPMA: null,
			opcao2DataPlantioAdjacencias: null,
			opcao2DataFlorescimentoAdjacencias: null,
			opcao4DataPlantioLPMA: null,
			opcao4DataFlorescimentoLPMA: null,
			opcao4DataPlantioAdjacencias: null,
			opcao4DataFlorescimentoAdjacencias: null,
			fcnOpcao2DataPlantioLPMA: null,
			fcnOpcao2DataFlorescimentoLPMA: null,
			fcnOpcao2DataPlantioAdjacencias: null,
			fcnOpcao2DataFlorescimentoAdjacencias: null,
			fcnOpcao4DataPlantioLPMA: null,
			fcnOpcao4DataFlorescimentoLPMA: null,
			fcnOpcao4DataPlantioAdjacencias: null,
			fcnOpcao4DataFlorescimentoAdjacencias: null,
			fcn30DataInicialPlantio: null,
			fcn30DataFinalPlantio: null,
			fcn30DataComunicacaoInicioPlantio: null,
			fcn30DataComunicacaoTerminoPlantio: null,
		},
	});

	// Estado para armazenar dados salvos temporariamente
	const [formDataCache, setFormDataCache] = React.useState<Record<string, any>>({});

	const [opcaoIsolamentoSelecionada, setOpcaoIsolamentoSelecionada] = React.useState<1 | 2 | 3 | 4>(1);
	const [opcaoIsolamentoSelecionadaFCN, setOpcaoIsolamentoSelecionadaFCN] = React.useState<1 | 2 | 3 | 4>(1);
	const [abaAtividadesPlantio, setAbaAtividadesPlantio] = React.useState<1 | 2 | 3 | 4 | 5>(1);
	const [abaAtividadesFcn30, setAbaAtividadesFcn30] = React.useState<1 | 2 | 3 | 4>(1);
	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = React.useState(1);
	const [quantidadeLinhasLimpezaFcn30, setQuantidadeLinhasLimpezaFcn30] = React.useState(1);

	// Estados para assinatura
	const [assinaturaColaboradores, setAssinaturaColaboradores] = useState<string | null>(null);
	const [assinaturaFcn30Colaboradores, setAssinaturaFcn30Colaboradores] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [mostrarAssinaturaFcn30, setMostrarAssinaturaFcn30] = useState(false);

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';
					
					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			
			// Carregar assinaturas salvas se existirem
			if (initialData?.assinaturaColaboradoresBase64) {
				setAssinaturaColaboradores(initialData.assinaturaColaboradoresBase64);
			}
			if (initialData?.fcn30AssinaturaColaboradoresBase64) {
				setAssinaturaFcn30Colaboradores(initialData.fcn30AssinaturaColaboradoresBase64);
			}
		} else {
			// Limpar estados ao fechar
			setAssinaturaColaboradores(null);
			setAssinaturaFcn30Colaboradores(null);
			setMostrarAssinatura(false);
			setMostrarAssinaturaFcn30(false);
		}
	}, [show, initialData]);

	const handleAssinar = (tipo: 'colaboradores' | 'fcn30') => {
		if (assinaturaUsuario) {
			if (tipo === 'colaboradores') {
				setAssinaturaColaboradores(assinaturaUsuario);
				setMostrarAssinatura(false);
			} else {
				setAssinaturaFcn30Colaboradores(assinaturaUsuario);
				setMostrarAssinaturaFcn30(false);
			}
		}
	};

	const handleRemoverAssinatura = (tipo: 'colaboradores' | 'fcn30') => {
		if (tipo === 'colaboradores') {
			setAssinaturaColaboradores(null);
			setMostrarAssinatura(false);
		} else {
			setAssinaturaFcn30Colaboradores(null);
			setMostrarAssinaturaFcn30(false);
		}
	};

	// Watch das mudanças do formulário
	const formValues = watchPlantio();
	const renderCheckbox = (
		name: keyof CadernoLPMAPlantio,
		label: string,
		props?: Omit<React.ComponentProps<typeof Form.Check>, 'type' | 'label' | 'checked' | 'onChange'>,
	) => (
		<Controller
			control={controlPlantio}
			name={name as any}
			render={({ value, onChange }) => (
				<Form.Check
					type="checkbox"
					label={label}
					checked={Boolean(value)}
					onChange={(event) => onChange(event.target.checked)}
					{...props}
				/>
			)}
		/>
	);

	const renderCampoComparativo = (
		label: string,
		aprovadoName: keyof CadernoLPMAPlantio,
		plantadoName: keyof CadernoLPMAPlantio,
		options?: { step?: number },
	) => (
		<Form.Row className="row-custom">
			<Form.Group as={Col} md={12} className="mb-3">
				<Form.Label>{label}</Form.Label>
				<div className="d-flex flex-column flex-md-row" style={{ gap: '12px' }}>
					<div className="flex-fill">
						<Form.Label className="text-muted small d-block">Aprov.</Form.Label>
						<Form.Control
							type="number"
							step={options?.step}
							ref={registerPlantio}
							name={aprovadoName as any}
							placeholder="Aprov."
						/>
					</div>
					<div className="flex-fill">
						<Form.Label className="text-muted small d-block">Plant.</Form.Label>
						<Form.Control
							type="number"
							step={options?.step}
							ref={registerPlantio}
							name={plantadoName as any}
							placeholder="Plant."
						/>
					</div>
				</div>
			</Form.Group>
		</Form.Row>
	);

	React.useEffect(() => {
		if (!show) {
			resetPlantio();
			setFormDataCache({});
			setQuantidadeLinhasLimpeza(1);
			setQuantidadeLinhasLimpezaFcn30(1);
			setOpcaoIsolamentoSelecionada(1);
			setOpcaoIsolamentoSelecionadaFCN(1);
			return;
		}

		if (!initialData) {
			resetPlantio();
			setFormDataCache({});
			setQuantidadeLinhasLimpeza(1);
			setQuantidadeLinhasLimpezaFcn30(1);
			setOpcaoIsolamentoSelecionada(1);
			setOpcaoIsolamentoSelecionadaFCN(1);
			return;
		}

		const {
			valores,
			quantidadeLinhasLimpeza,
			quantidadeLinhasLimpezaFcn30,
			opcaoIsolamento,
			opcaoIsolamentoFcn,
		} = transformarPlantioMilhoParaFormulario(initialData);

		setQuantidadeLinhasLimpeza(quantidadeLinhasLimpeza);
		setQuantidadeLinhasLimpezaFcn30(quantidadeLinhasLimpezaFcn30);
		setOpcaoIsolamentoSelecionada(opcaoIsolamento);
		setOpcaoIsolamentoSelecionadaFCN(opcaoIsolamentoFcn);

		const coordenadasPlantioMilho = [
			...COORDENADAS_PLANTIO_ROWS.flatMap((pair) => [pair[0].name, pair[1].name]),
			...COORDENADAS_FCN30_ROWS.flatMap((pair) => [pair[0].name, pair[1].name]),
		];
		coordenadasPlantioMilho.forEach((campo) => {
			const v = (valores as Record<string, unknown>)[campo];
			if (v !== undefined && v !== null && String(v).length > 0) {
				(valores as Record<string, unknown>)[campo] = formatCoordenadaDmsMask(String(v));
			}
		});

		resetPlantio(valores as CadernoLPMAPlantio);
		setFormDataCache(valores);
	}, [show, initialData, resetPlantio]);

	// Função para salvar dados da aba atual
	const salvarDadosDaAbaAtual = () => {
		const valoresAtuais = getValues();
		setFormDataCache((prevCache) => ({
			...prevCache,
			...valoresAtuais,
		}));
	};

	// Função para restaurar dados quando muda de aba
	const restaurarDadosDaAba = React.useCallback(() => {
		if (formDataCache && Object.keys(formDataCache).length > 0) {
			Object.entries(formDataCache).forEach(([key, value]) => {
				setValue(key as any, value);
			});
		}
	}, [formDataCache, setValue]);

	// Restaurar dados quando modal abre
	React.useEffect(() => {
		if (show) {
			restaurarDadosDaAba();
		}
	}, [show, restaurarDadosDaAba]);

	// Salvar dados quando muda de aba
	const handleMudarAba = (novaAba: 1 | 2 | 3 | 4 | 5) => {
		salvarDadosDaAbaAtual();
		setAbaAtividadesPlantio(novaAba);
		restaurarDadosDaAba();
	};

	// Salvar dados quando muda de aba FCN 30
	const handleMudarAbaFcn30 = (novaAba: 1 | 2 | 3 | 4) => {
		salvarDadosDaAbaAtual();
		setAbaAtividadesFcn30(novaAba);
		restaurarDadosDaAba();
	};

	const onSubmitPlantioForm = (info: CadernoLPMAPlantio) => {
		// IMPORTANTE: Se estamos na aba de limpeza, salve os dados dela também
		if (abaAtividadesPlantio === 4) {
			salvarDadosDaAbaAtual();
		}
		
		// IMPORTANTE: Salvar dados da aba atual do FCN 30 também
		salvarDadosDaAbaAtual();
		
		// IMPORTANTE: Salvar todos os dados atuais ANTES de enviar
		// Isso garante que campos de todas as abas sejam inclusos
		const valoresAtuais = getValues();
		
		// DEBUG: Log detalhado de tudo que está em valoresAtuais
		console.log('=== DEBUG getValues() ===');
		console.log('Todas as chaves em valoresAtuais:', Object.keys(valoresAtuais));
		console.log('valoresAtuais completo:', valoresAtuais);
		
		const dadosCompletos = {
			...formDataCache,
			...valoresAtuais,
			assinaturaColaboradoresBase64: assinaturaColaboradores || null,
			fcn30AssinaturaColaboradoresBase64: assinaturaFcn30Colaboradores || null,
		};

		// DEBUG: Mostrar dados antes de processar
		console.log('=== DEBUG formDataCache ===');
		console.log('formDataCache:', formDataCache);
		console.log('checklistPriorPlantio1CurtoPrazo:', dadosCompletos.checklistPriorPlantio1CurtoPrazo);
		console.log('checklistPriorPlantio1MilhoCrioulas:', dadosCompletos.checklistPriorPlantio1MilhoCrioulas);
		console.log('checklistPriorPlantio1MilhoNaoGM:', dadosCompletos.checklistPriorPlantio1MilhoNaoGM);
		console.log('CoordenadaP01:', dadosCompletos.coordenadaP01);
		console.log('TalhaoPlantio:', dadosCompletos.talhaoPlantio);
		
		// DEBUG: Verificar campos de limpeza
		console.log('Quantidade de linhas de limpeza:', quantidadeLinhasLimpeza);
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			console.log(`Limpeza linha ${i}:`, {
				nome: getNestedValue(dadosCompletos, `limpezaMaquinarioNome${i}`),
				dataEntrada: getNestedValue(dadosCompletos, `limpezaMaquinarioDataEntrada${i}`),
				dataSaida: getNestedValue(dadosCompletos, `limpezaMaquinarioDataSaida${i}`),
				metodo: getNestedValue(dadosCompletos, `limpezaMaquinarioMetodo${i}`),
				responsavel: getNestedValue(dadosCompletos, `limpezaMaquinarioResponsavel${i}`),
			});
		}
		
		// Converter strings para boolean nos campos do checklist pré-plantio 1
		if (typeof dadosCompletos.checklistPriorPlantio1CurtoPrazo === 'string') {
			dadosCompletos.checklistPriorPlantio1CurtoPrazo = dadosCompletos.checklistPriorPlantio1CurtoPrazo === 'true';
		}
		if (typeof dadosCompletos.checklistPriorPlantio1MilhoCrioulas === 'string') {
			dadosCompletos.checklistPriorPlantio1MilhoCrioulas = dadosCompletos.checklistPriorPlantio1MilhoCrioulas === 'true';
		}
		if (typeof dadosCompletos.checklistPriorPlantio1MilhoNaoGM === 'string') {
			dadosCompletos.checklistPriorPlantio1MilhoNaoGM = dadosCompletos.checklistPriorPlantio1MilhoNaoGM === 'true';
		}

		// CONSOLIDAR DADOS DE LIMPEZA EM JSON
		const limpezaArray = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = getNestedValue(dadosCompletos, `limpezaMaquinarioNome${i}`);
			const dataEntrada = getNestedValue(dadosCompletos, `limpezaMaquinarioDataEntrada${i}`);
			const dataSaida = getNestedValue(dadosCompletos, `limpezaMaquinarioDataSaida${i}`);
			const metodo = getNestedValue(dadosCompletos, `limpezaMaquinarioMetodo${i}`);
			const responsavel = getNestedValue(dadosCompletos, `limpezaMaquinarioResponsavel${i}`);

			// Apenas incluir no array se houver pelo menos um campo preenchido
			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpezaArray.push({
					nome,
					dataEntrada,
					dataSaida,
					metodo,
					responsavel
				});
			}
		}

		// Converter limpeza para JSON apenas se houver dados
		if (limpezaArray.length > 0) {
			dadosCompletos.limpezaMaquinariosJson = JSON.stringify(limpezaArray);
		}

		console.log('LimpezaMaquinariosJson:', dadosCompletos.limpezaMaquinariosJson);
		console.log('Limpeza array final:', limpezaArray);

		// CONSOLIDAR DADOS DE LIMPEZA FCN 30 EM JSON
		const limpezaFcn30Array = [];
		for (let i = 0; i < quantidadeLinhasLimpezaFcn30; i++) {
			const nome = getNestedValue(dadosCompletos, `fcn30LimpezaMaquinarioNome${i}`);
			const dataEntrada = getNestedValue(dadosCompletos, `fcn30LimpezaMaquinarioDataEntrada${i}`);
			const dataSaida = getNestedValue(dadosCompletos, `fcn30LimpezaMaquinarioDataSaida${i}`);
			const metodo = getNestedValue(dadosCompletos, `fcn30LimpezaMaquinarioMetodo${i}`);
			const responsavel = getNestedValue(dadosCompletos, `fcn30LimpezaMaquinarioResponsavel${i}`);

			// Apenas incluir no array se houver pelo menos um campo preenchido
			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpezaFcn30Array.push({
					nome,
					dataEntrada,
					dataSaida,
					metodo,
					responsavel
				});
			}
		}

		// Converter limpeza FCN 30 para JSON apenas se houver dados
		if (limpezaFcn30Array.length > 0) {
			dadosCompletos.fcn30LimpezaMaquinariosJson = JSON.stringify(limpezaFcn30Array);
		}

		console.log('Fcn30LimpezaMaquinariosJson:', dadosCompletos.fcn30LimpezaMaquinariosJson);
		console.log('Limpeza FCN 30 array final:', limpezaFcn30Array);

		dadosCompletos.tipoPlantioAprovado = tipoPlantioBooleansToStr(
			!!dadosCompletos.tipoPlantioAprovadoMecanico,
			!!dadosCompletos.tipoPlantioAprovadoManual
		);
		dadosCompletos.tipoPlantioRealizado = tipoPlantioBooleansToStr(
			!!dadosCompletos.tipoPlantioRealizadoMecanico,
			!!dadosCompletos.tipoPlantioRealizadoManual
		);
		dadosCompletos.fcn30TipoPlantioAprovado = tipoPlantioBooleansToStr(
			!!dadosCompletos.fcn30TipoPlantioAprovadoMecanico,
			!!dadosCompletos.fcn30TipoPlantioAprovadoManual
		);
		dadosCompletos.fcn30TipoPlantioRealizado = tipoPlantioBooleansToStr(
			!!dadosCompletos.fcn30TipoPlantioRealizadoMecanico,
			!!dadosCompletos.fcn30TipoPlantioRealizadoManual
		);
		
		onSubmit(dadosCompletos);
		onHide();
	};

	const opcoesBotoes = [
		{ valor: 1, label: 'Opção 1\n(Espacial)' },
		{ valor: 2, label: 'Opção 2\n(Temporal)' },
		{ valor: 3, label: 'Opção 3\n(Curto Prazo)' },
		{ valor: 4, label: 'Opção 4\n(Crioulas)' },
	];

	const abasAtividadesOpcoes = [
		{ valor: 1, label: 'Colaboradores' },
		{ valor: 2, label: 'Localização' },
		{ valor: 3, label: 'Coordenadas' },
		{ valor: 4, label: 'Limpeza' },
		{ valor: 5, label: 'Comunicação' },
	];

	const abasFcn30Opcoes = [
		{ valor: 1, label: 'Colaboradores' },
		{ valor: 2, label: 'Localização' },
		{ valor: 3, label: 'Coordenadas' },
		{ valor: 4, label: 'Limpeza/Comunicação' },
	];

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.PLANTIOS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmitPlantio(onSubmitPlantioForm)} autoComplete="off">

				{/* ===== INFORMAÇÕES GERAIS DO PLANTIO ===== */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><h2>Informações Gerais do Plantio</h2></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de início do plantio</Form.Label>
						<br />
						<Controller
							control={controlPlantio}
							name="dataInicioPlantio"
							render={({ onChange, value }) => (
								<DatePicker
									selected={value}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de término do plantio</Form.Label>
						<br />
						<Controller
							control={controlPlantio}
							name="dataTerminoPlantio"
							render={({ onChange, value }) => (
								<DatePicker
									selected={value}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>

				<hr className="my-4" />

				{/* ===== CHECKLIST PRÉ-PLANTIO 1 - AVALIAÇÃO/CONFERÊNCIA ISOLAMENTO ===== */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><h2>Checklist Pré-Plantio 1 – Avaliação/Conferência Isolamento</h2></Form.Label>
						<p><small className="text-danger">Esta avaliação/conferência deverá ser realizada obrigatoriamente antes do plantio da LPMA.</small></p>
					</Form.Group>
				</Form.Row>

				{/* Ponto Crítico a) */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>a) A LPMA a ser conduzida será de curto prazo (destruição obrigatória das parcelas e bordadura até o estádio fenológico V10)?</strong></Form.Label>
					</Form.Group>
				</Form.Row>

			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Controller
						control={controlPlantio}
						name="checklistPriorPlantio1CurtoPrazo"
						render={({ onChange, value }) => (
							<div className="d-flex">
								<div className="form-check mr-4">
									<input
										className="form-check-input"
										type="radio"
										name="checklistPriorPlantio1CurtoPrazo"
										id="checklistCurtoPrazoSim"
										checked={value === true}
										onChange={() => onChange(true)}
									/>
									<label className="form-check-label" htmlFor="checklistCurtoPrazoSim">
										Sim
									</label>
								</div>
								<div className="form-check">
									<input
										className="form-check-input"
										type="radio"
										name="checklistPriorPlantio1CurtoPrazo"
										id="checklistCurtoPrazoNao"
										checked={value === false}
										onChange={() => onChange(false)}
									/>
									<label className="form-check-label" htmlFor="checklistCurtoPrazoNao">
										Não
									</label>
								</div>
							</div>
						)}
					/>
				</Form.Group>
			</Form.Row>

				{/* Se "Sim" em a) */}
				{formValues?.checklistPriorPlantio1CurtoPrazo === true || formValues?.checklistPriorPlantio1CurtoPrazo === 'true' ? (
					<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
						<Form.Group as={Col} md={12}>
							<Form.Label><small><strong>Ações Necessárias para curto prazo:</strong></small></Form.Label>
							<ol>
								<li>A bordadura deverá conter, pelo menos, <strong>10 (dez) linhas ou 7 (sete) metros</strong> de milho não geneticamente modificado, ou de milho geneticamente modificado aprovado pela CTNBio, ao redor das parcelas experimentais, não sendo necessário qualquer isolamento temporal ou espacial adicional em relação a outros plantios comerciais, experimentais e crioulos de milho.</li>
								<li>Os experimentos, incluso a bordadura, deverão ser destruídos, <strong>no máximo, até as plantas atingirem o estádio vegetativo V10</strong>, a fim de garantir a total ausência de estruturas reprodutivas.</li>
								<li><strong>Não é necessário avaliar/conferir e responder os pontos críticos "b" e "c" abaixo.</strong></li>
							</ol>
						</Form.Group>
					</Form.Row>
				) : (
					<Form.Row className="row-custom mt-2 p-3 bg-light border-left-warning">
						<Form.Group as={Col} md={12}>
							<Form.Label><small><strong>Ações Necessárias para plantio de longo prazo:</strong></small></Form.Label>
							<p><strong>Obrigatório avaliar/conferir e responder os pontos críticos "b" e "c" abaixo.</strong></p>
						</Form.Group>
					</Form.Row>
				)}

				<hr className="my-3" />

				{/* Ponto Crítico b) - Só aparece se não for curto prazo */}
				{formValues?.checklistPriorPlantio1CurtoPrazo === false || formValues?.checklistPriorPlantio1CurtoPrazo === 'false' || !formValues?.checklistPriorPlantio1CurtoPrazo ? (
					<>
						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Label><strong>b) Existe plantio e/ou intenção de plantio de variedades de milho crioulas nas adjacências da área onde será conduzida a LPMA?</strong></Form.Label>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Controller
									control={controlPlantio}
									name="checklistPriorPlantio1MilhoCrioulas"
									render={({ onChange, value }) => (
										<div className="d-flex">
											<div className="form-check mr-4">
												<input
													className="form-check-input"
													type="radio"
													name="checklistPriorPlantio1MilhoCrioulas"
													id="checklistMilhoCrioulassSim"
													checked={value === true}
													onChange={() => onChange(true)}
												/>
												<label className="form-check-label" htmlFor="checklistMilhoCrioulassSim">
													Sim
												</label>
											</div>
											<div className="form-check">
												<input
													className="form-check-input"
													type="radio"
													name="checklistPriorPlantio1MilhoCrioulas"
													id="checklistMilhoCrioulasNao"
													checked={value === false}
													onChange={() => onChange(false)}
												/>
												<label className="form-check-label" htmlFor="checklistMilhoCrioulasNao">
													Não
												</label>
											</div>
										</div>
									)}
								/>
							</Form.Group>
						</Form.Row>

						{/* Se "Sim" em b) */}
						{formValues?.checklistPriorPlantio1MilhoCrioulas === true || formValues?.checklistPriorPlantio1MilhoCrioulas === 'true' ? (
							<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
								<Form.Group as={Col} md={12}>
									<Form.Label><small><strong>Ações Necessárias:</strong></small></Form.Label>
									<ol>
										<li>Anotar as distâncias e comunicar imediatamente o Técnico Principal e a CIBio BASF.</li>
										<li>Estabelecer ao redor das parcelas experimentais da LPMA, bordadura com, pelo menos, <strong>10 (dez) linhas ou 7 (sete) metros</strong> de milho não geneticamente modificado, manter <strong>distância mínima de 200 (duzentos) metros</strong> (isolamento espacial) e respeitar período mínimo de <strong>40 (quarenta) dias</strong> entre as datas de florescimento da LPMA e os cultivos de variedades crioulas de milho (isolamento temporal).</li>
										<li>Registrar no formulário de definição de Isolamento e Bordadura a(s) data(s) de plantio da variedade de milho crioula.</li>
									</ol>
								</Form.Group>
							</Form.Row>
						) : null}

						{formValues?.checklistPriorPlantio1MilhoCrioulas === true || formValues?.checklistPriorPlantio1MilhoCrioulas === 'true' ? (
							<Form.Row className="row-custom mt-2">
								<Form.Group as={Col} md={6}>
									<Form.Label>Distância registrada (metros)</Form.Label>
									<Form.Control
										type="number"
										step="0.01"
										ref={registerPlantio}
										name="checklistPriorPlantio1DistanciaMilhoCrioulas"
										placeholder="Insira a distância em metros"
									/>
								</Form.Group>
								<Form.Group as={Col} md={6}>
									<Form.Label>Data de plantio da variedade crioula</Form.Label>
									<br />
									<Controller
										control={controlPlantio}
										name="checklistPriorPlantio1DataPlantioMilhoCrioulas"
										render={({ onChange, value }) => (
											<DatePicker
												selected={value}
												onChange={onChange}
												className="input-custom-to-date"
												locale="pt-BR"
												dateFormat="dd/MM/yyyy"
												placeholderText="Selecionar a data"
											/>
										)}
									/>
								</Form.Group>
							</Form.Row>
						) : null}

						<hr className="my-3" />

						{/* Ponto Crítico c) */}
						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Label><strong>c) Existe o plantio e/ou intenção de plantio de variedades de milho não geneticamente modificado nas adjacências da área onde será conduzida a LPMA?</strong></Form.Label>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Controller
									control={controlPlantio}
									name="checklistPriorPlantio1MilhoNaoGM"
									render={({ onChange, value }) => (
										<div className="d-flex">
											<div className="form-check mr-4">
												<input
													className="form-check-input"
													type="radio"
													name="checklistPriorPlantio1MilhoNaoGM"
													id="checklistMilhoNaoGMSim"
													checked={value === true}
													onChange={() => onChange(true)}
												/>
												<label className="form-check-label" htmlFor="checklistMilhoNaoGMSim">
													Sim
												</label>
											</div>
											<div className="form-check">
												<input
													className="form-check-input"
													type="radio"
													name="checklistPriorPlantio1MilhoNaoGM"
													id="checklistMilhoNaoGMNao"
													checked={value === false}
													onChange={() => onChange(false)}
												/>
												<label className="form-check-label" htmlFor="checklistMilhoNaoGMNao">
													Não
												</label>
											</div>
										</div>
									)}
								/>
							</Form.Group>
						</Form.Row>

						{/* Se "Sim" em c) */}
						{formValues?.checklistPriorPlantio1MilhoNaoGM === true || formValues?.checklistPriorPlantio1MilhoNaoGM === 'true' ? (
							<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
								<Form.Group as={Col} md={12}>
									<Form.Label><small><strong>Ações Necessárias:</strong></small></Form.Label>
									<ol>
										<li>Anotar as distâncias e comunicar imediatamente o Técnico Principal e a CIBio BASF.</li>
										<li>Para isolamento temporal (40 dias), caso seja utilizado na bordadura milho GM aprovado comercialmente pela CTNBio, a requerente também deverá observar o período mínimo de <strong>40 dias</strong> entre datas de florescimento da bordadura da LPMA e outros plantios comerciais de milho não GM.</li>
										<li>Registrar no formulário de definição de Isolamento e Bordadura a(s) data(s) de plantio do milho não geneticamente modificado.</li>
									</ol>
								</Form.Group>
							</Form.Row>
						) : null}

						{formValues?.checklistPriorPlantio1MilhoNaoGM === true || formValues?.checklistPriorPlantio1MilhoNaoGM === 'true' ? (
							<Form.Row className="row-custom mt-2">
								<Form.Group as={Col} md={6}>
									<Form.Label>Distância registrada (metros)</Form.Label>
									<Form.Control
										type="number"
										step="0.01"
										ref={registerPlantio}
										name="checklistPriorPlantio1DistanciaMilhoNaoGM"
										placeholder="Insira a distância em metros"
									/>
								</Form.Group>
								<Form.Group as={Col} md={6}>
									<Form.Label>Data de plantio do milho não GM</Form.Label>
									<br />
									<Controller
										control={controlPlantio}
										name="checklistPriorPlantio1DataPlantioMilhoNaoGM"
										render={({ onChange, value }) => (
											<DatePicker
												selected={value}
												onChange={onChange}
												className="input-custom-to-date"
												locale="pt-BR"
												dateFormat="dd/MM/yyyy"
												placeholderText="Selecionar a data"
											/>
										)}
									/>
								</Form.Group>
							</Form.Row>
						) : null}

						<hr className="my-4" />
					</>
				) : null}

			

				{/* ===== ISOLAMENTO E BORDADURA ===== */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><h2>Formulário - Isolamento e Bordadura</h2></Form.Label>
						<p>Este formulário deve ser preenchido por ocasião da definição do isolamento/bordadura</p>
					</Form.Group>
				</Form.Row>

				{/* Seleção de Opções */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{opcoesBotoes.map((opcao) => (
								<Button
									key={opcao.valor}
									title={opcao.label}
									contained={opcaoIsolamentoSelecionada === opcao.valor}
									className={opcaoIsolamentoSelecionada === opcao.valor ? 'active' : ''}
									type="button"
									onClick={() => setOpcaoIsolamentoSelecionada(opcao.valor as 1 | 2 | 3 | 4)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				<hr className="my-3" />

				{/* OPÇÃO 1 */}
				{opcaoIsolamentoSelecionada === 1 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Isolamento adotado/mantida LPMA (metros)</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao1DistanciaMetros"
									placeholder="Digite a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Linhas</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao1BordaduraLinhas"
									placeholder="Digite o número de linhas"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Metros</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao1BordaduraMetros"
									placeholder="Digite a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Variedade plantada na bordadura</Form.Label>
								<Form.Control
									ref={registerPlantio}
									name="opcao1VariedadeBordadura"
									placeholder="Digite a variedade"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao1VariedadeBordaduraGM"
									id="opcao1GM"
									value="true"
									label="GM"
								/>
							</Form.Group>
						</Form.Row>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao1VariedadeBordaduraGM"
									id="opcao1NaoGM"
									value="false"
									label="Não GM"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* OPÇÃO 2 */}
				{opcaoIsolamentoSelecionada === 2 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data plantio da LPMA</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao2DataPlantioLPMA"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data florescimento LPMA</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao2DataFlorescimentoLPMA"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data plantio/milho adjacências</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao2DataPlantioAdjacencias"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data florescimento milho/adjacências</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao2DataFlorescimentoAdjacencias"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Intervalo de florescimento (dias)</Form.Label>
								<Form.Control
									type="number"
									ref={registerPlantio}
									name="opcao2IntervaloFlorescimentoDias"
									placeholder="Digite o número de dias"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Linhas</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao2BordaduraLinhas"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Metros</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao2BordaduraMetros"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Variedade plantada na bordadura</Form.Label>
								<Form.Control ref={registerPlantio} name="opcao2VariedadeBordadura" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao2VariedadeBordaduraGM"
									id="opcao2GM"
									value="true"
									label="GM"
								/>
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao2VariedadeBordaduraGM"
									id="opcao2NaoGM"
									value="false"
									label="Não GM"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* OPÇÃO 3 */}
				{opcaoIsolamentoSelecionada === 3 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Linhas</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao3BordaduraLinhas"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Metros</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao3BordaduraMetros"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Variedade plantada na bordadura</Form.Label>
								<Form.Control ref={registerPlantio} name="opcao3VariedadeBordadura" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao3VariedadeBordaduraGM"
									id="opcao3GM"
									value="true"
									label="GM"
								/>
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Check
									type="radio"
									ref={registerPlantio}
									name="opcao3VariedadeBordaduraGM"
									id="opcao3NaoGM"
									value="false"
									label="Não GM"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* OPÇÃO 4 */}
				{opcaoIsolamentoSelecionada === 4 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Isolamento adotado/mantida LPMA (metros)</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao4DistanciaMetros"
									placeholder="Digite a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Linhas</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao4BordaduraLinhas"
									placeholder="Digite o número de linhas"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Bordadura - Metros</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="opcao4BordaduraMetros"
									placeholder="Digite a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Variedade plantada na bordadura</Form.Label>
								<Form.Control
									ref={registerPlantio}
									name="opcao4VariedadeBordadura"
									placeholder="Digite a variedade"
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data plantio da LPMA</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao4DataPlantioLPMA"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data florescimento LPMA</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao4DataFlorescimentoLPMA"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data plantio/variedade milho crioula nas adjacências</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao4DataPlantioAdjacencias"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data florescimento milho crioula nas adjacências</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="opcao4DataFlorescimentoAdjacencias"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Intervalo de florescimento (dias)</Form.Label>
								<Form.Control
									type="number"
									ref={registerPlantio}
									name="opcao4IntervaloFlorescimentoDias"
									placeholder="Digite o número de dias"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Variedade plantada na bordadura</Form.Label>
								<Form.Control ref={registerPlantio} name="opcao4VariedadeBordadura" />
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Observações complementares</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									ref={registerPlantio}
									name="opcao4Observacoes"
									placeholder="Digite observações se necessário"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				<hr className="my-4" />

				{/* ===== FORMULÁRIO - REGISTRO DE ATIVIDADES/INFORMAÇÕES DO PLANTIO ===== */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><h2>Formulário - Registro de atividades/informações do Plantio</h2></Form.Label>
					</Form.Group>
				</Form.Row>

				{/* Seleção de Abas */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{abasAtividadesOpcoes.map((aba) => (
								<Button
									key={aba.valor}
									title={aba.label}
									contained={abaAtividadesPlantio === aba.valor}
									className={abaAtividadesPlantio === aba.valor ? 'active' : ''}
									type="button"
									onClick={() => handleMudarAba(aba.valor as 1 | 2 | 3 | 4 | 5)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				<hr className="my-3" />

				{/* ABA 1 - COLABORADORES */}
				{abaAtividadesPlantio === 1 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Colaborador responsável pelo plantio</Form.Label>
								<Form.Control
									ref={registerPlantio}
									name="colaboradorResponsavelPlantio"
									placeholder="Digite o nome do colaborador"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data de registro do plantio</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="dataRegistroPlantio"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>
									<strong>Assinatura dos Colaboradores</strong>
									{assinaturaColaboradores && (
										<i className="fas fa-check-circle" style={{ 
											color: '#28a745', 
											marginLeft: '10px',
											fontSize: '18px'
										}}></i>
									)}
								</Form.Label>
								
								{!mostrarAssinatura ? (
									<div 
										onClick={() => setMostrarAssinatura(true)}
										style={{
											border: assinaturaColaboradores ? '2px solid #28a745' : '2px dashed #6c757d',
											borderRadius: '8px',
											padding: '30px 20px',
											textAlign: 'center',
											cursor: 'pointer',
											backgroundColor: assinaturaColaboradores ? '#f0f9f4' : '#f8f9fa',
											transition: 'all 0.3s',
											position: 'relative',
											overflow: 'hidden'
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = assinaturaColaboradores ? '#e6f4ea' : '#e9ecef';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = assinaturaColaboradores ? '#f0f9f4' : '#f8f9fa';
										}}
									>
										{assinaturaColaboradores ? (
											<>
												<div style={{
													filter: 'blur(3px)',
													opacity: 0.6,
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
													minHeight: '60px'
												}}>
													<img 
														src={assinaturaColaboradores} 
														alt="Assinatura" 
														style={{ 
															maxWidth: '200px',
															maxHeight: '60px',
															height: 'auto'
														}}
													/>
												</div>
												<div style={{ 
													position: 'absolute',
													top: '50%',
													left: '50%',
													transform: 'translate(-50%, -50%)',
													color: '#28a745',
													fontWeight: 600,
													fontSize: '14px',
													backgroundColor: 'rgba(255, 255, 255, 0.95)',
													padding: '5px 15px',
													borderRadius: '20px',
													border: '1px solid #28a745'
												}}>
													✓ Assinado - Clique para visualizar ou alterar
												</div>
											</>
										) : (
											<>
												<i className="fas fa-signature" style={{ 
													fontSize: '24px', 
													color: '#6c757d', 
													marginBottom: '10px',
													display: 'block'
												}}></i>
												<div style={{ color: '#6c757d', fontWeight: 500 }}>
													Clique aqui para assinar
												</div>
												<small className="text-muted d-block mt-2">
													Sua assinatura cadastrada será aplicada
												</small>
											</>
										)}
									</div>
								) : (
									<Card style={{ marginTop: '10px' }}>
										<Card.Body>
											{assinaturaUsuario ? (
												<>
													<div style={{ textAlign: 'center', marginBottom: '15px' }}>
														<strong>Sua Assinatura:</strong>
													</div>
													<div style={{ 
														display: 'flex', 
														justifyContent: 'center',
														padding: '20px',
														backgroundColor: '#f8f9fa',
														borderRadius: '4px',
														marginBottom: '15px'
													}}>
														<img 
															src={assinaturaUsuario} 
															alt="Sua assinatura" 
															style={{ 
																maxWidth: '300px',
																maxHeight: '100px',
																height: 'auto',
																border: '1px solid #dee2e6',
																borderRadius: '4px',
																padding: '10px',
																backgroundColor: '#fff'
															}}
														/>
													</div>
													<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
														<Button 
															title="Cancelar" 
															contained={false} 
															type="button" 
															onClick={() => setMostrarAssinatura(false)}
														/>
														{assinaturaColaboradores && (
															<Button 
																title="Remover Assinatura" 
																contained={false} 
																type="button" 
																onClick={() => handleRemoverAssinatura('colaboradores')}
															/>
														)}
														<Button 
															title={assinaturaColaboradores ? "Alterar Assinatura" : "Confirmar Assinatura"}
															contained 
															type="button" 
															onClick={() => handleAssinar('colaboradores')}
														/>
													</div>
												</>
											) : (
												<Alert variant="warning">
													Você ainda não possui uma assinatura cadastrada. 
													<br />
													Acesse <strong>Caderno LPMA → Registros → Assinaturas</strong> para cadastrar.
												</Alert>
											)}
										</Card.Body>
									</Card>
								)}
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data inicial do plantio</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="dataInicioColaboradores"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data final do plantio</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="dataFimColaboradores"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

			{/* ABA 2 - LOCALIZAÇÃO */}
			{abaAtividadesPlantio === 2 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Localização (talhão), tipo de plantio e informações técnicas sobre área e delineamento aprovado LPMA x efetivamente plantado</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={4}>
							<Form.Label>Talhão</Form.Label>
							<Form.Control
								ref={registerPlantio}
								name="talhaoPlantio"
								placeholder="Digite o talhão"
							/>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>Tipo de plantio APROVADO</Form.Label>
							<div className="d-flex" style={{ gap: '12px' }}>
								{renderCheckbox('tipoPlantioAprovadoMecanico', 'Mecânico', { id: 'tipoPlantioAprovadoMecanico' })}
								{renderCheckbox('tipoPlantioAprovadoManual', 'Manual', { id: 'tipoPlantioAprovadoManual' })}
							</div>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>Tipo de plantio REALIZADO</Form.Label>
							<div className="d-flex" style={{ gap: '12px' }}>
								{renderCheckbox('tipoPlantioRealizadoMecanico', 'Mecânico', { id: 'tipoPlantioRealizadoMecanico' })}
								{renderCheckbox('tipoPlantioRealizadoManual', 'Manual', { id: 'tipoPlantioRealizadoManual' })}
							</div>
						</Form.Group>
					</Form.Row>

					<hr className="my-3" />

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Informações técnicas (aprovação x plantio)</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Experimento e/ou Ensaio</Form.Label>
							<Form.Control
								ref={registerPlantio}
								name="experimentoEnsaio"
								placeholder="Informe o experimento/ensaio"
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Tipo de material (entradas)</Form.Label>
							<Form.Control
								ref={registerPlantio}
								name="tipoMaterialEntradas"
								placeholder="Descreva o material"
							/>
						</Form.Group>
					</Form.Row>

					{renderCampoComparativo(
						'n.º Eventos (entradas)',
						'numeroEventosAprovado',
						'numeroEventosPlantado',
					)}
					{renderCampoComparativo('n.º Tratamentos', 'numeroTratamentosAprovado', 'numeroTratamentosPlantado')}
					{renderCampoComparativo('n.º Repetições', 'numeroRepeticoesAprovado', 'numeroRepeticoesPlantado')}
					{renderCampoComparativo('n.º Parcelas', 'numeroParcelasAprovado', 'numeroParcelasPlantado')}
					{renderCampoComparativo(
						'Tamanho Parcelas (m²)',
						'tamanhoParcelasAprovado',
						'tamanhoParcelasPlantado',
						{ step: 0.01 },
					)}
					{renderCampoComparativo('Área Total (m²)', 'areaTotalAprovado', 'areaTotalPlantado', { step: 0.01 })}

					<hr className="my-3" />

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Resumo/Total geral LPMA (m²)</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					{renderCampoComparativo('OGM (m²)', 'ogmAprovadoM2', 'ogmPlantadoM2', { step: 0.01 })}
					{renderCampoComparativo(
						'Comercial/WT (m²)',
						'comercialWtAprovadoM2',
						'comercialWtPlantadoM2',
						{ step: 0.01 },
					)}
					{renderCampoComparativo(
						'OGM + Comercial/WT (m²)',
						'ogmComercialWtAprovadoM2',
						'ogmComercialWtPlantadoM2',
						{ step: 0.01 },
					)}
				</>
			)}

				{/* ABA 3 - COORDENADAS */}
				{abaAtividadesPlantio === 3 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<small className="text-muted">Formato: graus, minutos e segundos – Ex.: P1: 15° 31' 10.11" - W 54° 23' 54.17"</small>
							</Form.Group>
						</Form.Row>

						{COORDENADAS_PLANTIO_ROWS.map((pair) => (
							<Form.Row className="row-custom" key={pair[0].name}>
								<CoordenadaDmsField
									control={controlPlantio}
									name={pair[0].name as string}
									label={pair[0].label}
								/>
								<CoordenadaDmsField
									control={controlPlantio}
									name={pair[1].name as string}
									label={pair[1].label}
								/>
							</Form.Row>
						))}
					</>
				)}

				{/* ABA 4 - LIMPEZA DE MAQUINÁRIOS */}
				{abaAtividadesPlantio === 4 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="table-responsive">
									<table className="table table-bordered table-sm">
										<thead className="table-light">
											<tr>
												<th>Nome e código do maquinário/equipamento utilizado</th>
												<th colSpan={2}>Datas de limpeza</th>
												<th>Método de limpeza</th>
												<th>Responsável</th>
											</tr>
											<tr>
												<th></th>
												<th>Antes ENTRADA</th>
												<th>Antes SAÍDA</th>
												<th></th>
												<th></th>
											</tr>
										</thead>
										<tbody>
											{[...Array(quantidadeLinhasLimpeza)].map((_, index) => (
												<tr key={index}>
													<td>
														<Form.Control
															type="text"
															size="sm"
															placeholder="Nome e código"
															className="mb-2"
															ref={registerPlantio}
															name={`limpezaMaquinarioNome${index}`}
														/>
													</td>
													<td>
														<Controller
															control={controlPlantio}
															name={`limpezaMaquinarioDataEntrada${index}`}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={value}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="/ /"
																	inline={false}
																/>
															)}
														/>
													</td>
													<td>
														<Controller
															control={controlPlantio}
															name={`limpezaMaquinarioDataSaida${index}`}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={value}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="/ /"
																	inline={false}
																/>
															)}
														/>
													</td>
													<td>
														<Form.Control
															type="text"
															size="sm"
															placeholder="Método"
															className="mb-2"
															ref={registerPlantio}
															name={`limpezaMaquinarioMetodo${index}`}
														/>
													</td>
													<td>
														<Form.Control
															type="text"
															size="sm"
															placeholder="Responsável"
															className="mb-2"
															ref={registerPlantio}
															name={`limpezaMaquinarioResponsavel${index}`}
														/>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</Form.Group>
						</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Button
								title="Adicionar Equipamento"
								contained={true}
								type="button"
								onClick={() => setQuantidadeLinhasLimpeza(quantidadeLinhasLimpeza + 1)}
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Observações sobre limpeza de maquinários</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									ref={registerPlantio}
									name="observacaoLimpezaMaquinarios"
									placeholder="Comentários adicionais sobre a limpeza"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* ABA 5 - COMUNICAÇÃO */}
				{abaAtividadesPlantio === 5 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data de comunicação do início do plantio à CIBio</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="dataComunicacaoInicioPlantio"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data de comunicação do término do plantio à CIBio</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="dataComunicacaoTerminoPlantio"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				<hr className="my-4" />

				{/* ===== CHECKLIST - PLANTIO 1 ===== */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Checklist - Plantio 1</h2>
						</Form.Label>
						<p>As perguntas abaixo são pontos de checagem visando o atendimento dos requisitos de biossegurança:</p>
					</Form.Group>
				</Form.Row>

				{/* Item a */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'placaBiossegurancaPreenchia',
							'a. A placa de biossegurança foi preenchida e instalada nas quadras/talhões ocupados pela LPMA?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={registerPlantio}
							name="observacaoPlacaBiossegurancaPreenchia"
							placeholder="Comentários"
						/>
					</Form.Group>
				</Form.Row>

				{/* Item b */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'comunicadoPlantio',
							'b. As estacas de delimitação foram colocadas nas extremidades da LPMA?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={registerPlantio}
							name="observacaoComunicadoPlantio"
							placeholder="Comentários"
						/>
					</Form.Group>
				</Form.Row>

				{/* Item c */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'comunicadoPlantioComCroqui',
							'c. O comunicado de plantio com o croqui, mapa e coordenadas geográficas atualizadas foi encaminhado para CIBio?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={registerPlantio}
							name="observacaoComunicadoPlantioComCroqui"
							placeholder="Comentários"
						/>
					</Form.Group>
				</Form.Row>

				{/* Item d */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'envelopesDestruidos',
							'd. Os envelopes de sementes e embalagens remanescentes do plantio da LPMA foram devidamente destruídos/descartados, e o registro foi realizado na página de destruição e descarte?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={registerPlantio}
							name="observacaoEnvelopesDestruidos"
							placeholder="Comentários"
						/>
					</Form.Group>
				</Form.Row>

				{/* Item e */}
				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'roupasEquipamentosLimpos',
							'e. Roupas, calçados assim como maquinários, equipamentos e utensílios utilizados pela equipe foram limpos e inspecionados ANTES de entrar e ANTES de sair da área da LPMA?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={registerPlantio}
							name="observacaoRoupasEquipamentosLimpos"
							placeholder="Comentários"
						/>
					</Form.Group>
				</Form.Row>

			{/* ===== CHECKLIST PRÉ-PLANTIO 2 - AVALIAÇÃO/CONFERÊNCIA ISOLAMENTO ===== */}
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Form.Label><h2>Checklist Pré-Plantio 2 – Avaliação/Conferência Isolamento</h2></Form.Label>
					<p><small className="text-danger">Esta avaliação/conferência deverá ser realizada obrigatoriamente antes do plantio da LPMA.</small></p>
				</Form.Group>
			</Form.Row>

			{/* Ponto Crítico a) */}
			<Form.Row className="row-custom mt-3">
				<Form.Group as={Col} md={12}>
					<Form.Label><strong>a) A LPMA a ser conduzida será de curto prazo (destruição obrigatória das parcelas e bordadura até o estádio fenológico V10)?</strong></Form.Label>
				</Form.Group>
			</Form.Row>

			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<div className="d-flex">
						<div className="form-check mr-4">
							<input
								className="form-check-input"
								type="radio"
								ref={registerPlantio}
								name="checklistPrePlantio2CurtoPrazo"
								id="checklistPrePlantio2CurtoPrazoSim"
								value="true"
							/>
							<label className="form-check-label" htmlFor="checklistPrePlantio2CurtoPrazoSim">Sim</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								ref={registerPlantio}
								name="checklistPrePlantio2CurtoPrazo"
								id="checklistPrePlantio2CurtoPrazoNao"
								value="false"
							/>
							<label className="form-check-label" htmlFor="checklistPrePlantio2CurtoPrazoNao">Não</label>
						</div>
					</div>
				</Form.Group>
			</Form.Row>

			{/* Se "Sim" em a) */}
			{formValues?.checklistPrePlantio2CurtoPrazo === true || formValues?.checklistPrePlantio2CurtoPrazo === 'true' ? (
				<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
					<Form.Group as={Col} md={12}>
						<Form.Label><small><strong>Ações Necessárias para curto prazo:</strong></small></Form.Label>
						<ol>
							<li>A bordadura deverá conter, pelo menos, <strong>10 (dez) linhas ou 7 (sete) metros</strong> de milho não geneticamente modificado, ou de milho geneticamente modificado aprovado pela CTNBio, ao redor das parcelas experimentais, não sendo necessário qualquer isolamento temporal ou espacial adicional em relação a outros plantios comerciais, experimentais e crioulos de milho.</li>
							<li>Os experimentos, incluso a bordadura, deverão ser destruídos, <strong>no máximo, até as plantas atingirem o estádio vegetativo V10</strong>, a fim de garantir a total ausência de estruturas reprodutivas.</li>
							<li><strong>Não é necessário avaliar/conferir e responder os pontos críticos "b" e "c" abaixo.</strong></li>
						</ol>
					</Form.Group>
				</Form.Row>
			) : (
				<Form.Row className="row-custom mt-2 p-3 bg-light border-left-warning">
					<Form.Group as={Col} md={12}>
						<Form.Label><small><strong>Ações Necessárias para plantio de longo prazo:</strong></small></Form.Label>
						<p><strong>Obrigatório avaliar/conferir e responder os pontos críticos "b" e "c" abaixo.</strong></p>
					</Form.Group>
				</Form.Row>
			)}

			<hr className="my-3" />

			{/* Ponto Crítico b) - Só aparece se não for curto prazo */}
			{formValues?.checklistPrePlantio2CurtoPrazo === false || formValues?.checklistPrePlantio2CurtoPrazo === 'false' || !formValues?.checklistPrePlantio2CurtoPrazo ? (
				<>
					<Form.Row className="row-custom mt-3">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>b) Existe plantio e/ou intenção de plantio de variedades de milho crioulas nas adjacências da área onde será conduzida a LPMA?</strong></Form.Label>
						</Form.Group>
					</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="d-flex">
									<div className="form-check mr-4">
										<input
											className="form-check-input"
											type="radio"
											ref={registerPlantio}
											name="checklistPrePlantio2MilhoCrioulas"
											id="checklistPrePlantio2MilhoCrioulassSim"
											value="true"
										/>
										<label className="form-check-label" htmlFor="checklistPrePlantio2MilhoCrioulassSim">Sim</label>
									</div>
									<div className="form-check">
										<input
											className="form-check-input"
											type="radio"
											ref={registerPlantio}
											name="checklistPrePlantio2MilhoCrioulas"
											id="checklistPrePlantio2MilhoCrioulasNao"
											value="false"
										/>
										<label className="form-check-label" htmlFor="checklistPrePlantio2MilhoCrioulasNao">Não</label>
									</div>
								</div>
							</Form.Group>
						</Form.Row>

					{/* Se "Sim" em b) */}
					{formValues?.checklistPrePlantio2MilhoCrioulas === true || formValues?.checklistPrePlantio2MilhoCrioulas === 'true' ? (
						<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
							<Form.Group as={Col} md={12}>
								<Form.Label><small><strong>Ações Necessárias:</strong></small></Form.Label>
								<ol>
									<li>Anotar as distâncias e comunicar imediatamente o Técnico Principal e a CIBio BASF.</li>
									<li>Estabelecer ao redor das parcelas experimentais da LPMA, bordadura com, pelo menos, <strong>10 (dez) linhas ou 7 (sete) metros</strong> de milho não geneticamente modificado, manter <strong>distância mínima de 200 (duzentos) metros</strong> (isolamento espacial) e respeitar período mínimo de <strong>40 (quarenta) dias</strong> entre as datas de florescimento da LPMA e os cultivos de variedades crioulas de milho (isolamento temporal).</li>
									<li>Registrar no formulário de definição de Isolamento e Bordadura a(s) data(s) de plantio da variedade de milho crioula.</li>
								</ol>
							</Form.Group>
						</Form.Row>
					) : null}

					{formValues?.checklistPrePlantio2MilhoCrioulas === true || formValues?.checklistPrePlantio2MilhoCrioulas === 'true' ? (
						<Form.Row className="row-custom mt-2">
							<Form.Group as={Col} md={6}>
								<Form.Label>Distância registrada (metros)</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="checklistPrePlantio2DistanciaMilhoCrioulas"
									placeholder="Insira a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data de plantio da variedade crioula</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="checklistPrePlantio2DataPlantioMilhoCrioulas"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>
					) : null}

					<hr className="my-3" />

					{/* Ponto Crítico c) */}
					<Form.Row className="row-custom mt-3">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>c) Existe o plantio e/ou intenção de plantio de variedades de milho não geneticamente modificado nas adjacências da área onde será conduzida a LPMA?</strong></Form.Label>
						</Form.Group>
					</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="d-flex">
									<div className="form-check mr-4">
										<input
											className="form-check-input"
											type="radio"
											ref={registerPlantio}
											name="checklistPrePlantio2MilhoNaoGM"
											id="checklistPrePlantio2MilhoNaoGMSim"
											value="true"
										/>
										<label className="form-check-label" htmlFor="checklistPrePlantio2MilhoNaoGMSim">Sim</label>
									</div>
									<div className="form-check">
										<input
											className="form-check-input"
											type="radio"
											ref={registerPlantio}
											name="checklistPrePlantio2MilhoNaoGM"
											id="checklistPrePlantio2MilhoNaoGMNao"
											value="false"
										/>
										<label className="form-check-label" htmlFor="checklistPrePlantio2MilhoNaoGMNao">Não</label>
									</div>
								</div>
							</Form.Group>
						</Form.Row>

					{/* Se "Sim" em c) */}
					{formValues?.checklistPrePlantio2MilhoNaoGM === true || formValues?.checklistPrePlantio2MilhoNaoGM === 'true' ? (
						<Form.Row className="row-custom mt-2 p-3 bg-light border-left-success">
							<Form.Group as={Col} md={12}>
								<Form.Label><small><strong>Ações Necessárias:</strong></small></Form.Label>
								<ol>
									<li>Anotar as distâncias e comunicar imediatamente o Técnico Principal e a CIBio BASF.</li>
									<li>Para isolamento temporal (40 dias), caso seja utilizado na bordadura milho GM aprovado comercialmente pela CTNBio, a requerente também deverá observar o período mínimo de <strong>40 dias</strong> entre datas de florescimento da bordadura da LPMA e outros plantios comerciais de milho não GM.</li>
									<li>Registrar no formulário de definição de Isolamento e Bordadura a(s) data(s) de plantio do milho não geneticamente modificado.</li>
								</ol>
							</Form.Group>
						</Form.Row>
					) : null}

					{formValues?.checklistPrePlantio2MilhoNaoGM === true || formValues?.checklistPrePlantio2MilhoNaoGM === 'true' ? (
						<Form.Row className="row-custom mt-2">
							<Form.Group as={Col} md={6}>
								<Form.Label>Distância registrada (metros)</Form.Label>
								<Form.Control
									type="number"
									step="0.01"
									ref={registerPlantio}
									name="checklistPrePlantio2DistanciaMilhoNaoGM"
									placeholder="Insira a distância em metros"
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data de plantio do milho não GM</Form.Label>
								<br />
								<Controller
									control={controlPlantio}
									name="checklistPrePlantio2DataPlantioMilhoNaoGM"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>
					) : null}

					<hr className="my-4" />
				</>
			) : null}


			{/* ===== NOVA SEÇÃO: FCN 27/28 - Definição/Registro do Isolamento e Bordadura ===== */}
			<hr className="my-4" />
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Form.Label><h2>Formulário – Definição/Registro do Isolamento e Bordadura 2</h2></Form.Label>
					<p>Este formulário deve ser preenchido por ocasião da definição do isolamento/bordadura.</p>
				</Form.Group>
			</Form.Row>

			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
						{opcoesBotoes.map((opcao) => (
							<Button
								key={`fcn-${opcao.valor}`}
								title={opcao.label}
								contained={opcaoIsolamentoSelecionadaFCN === opcao.valor}
								className={opcaoIsolamentoSelecionadaFCN === opcao.valor ? 'active' : ''}
								type="button"
								onClick={() => setOpcaoIsolamentoSelecionadaFCN(opcao.valor as 1 | 2 | 3 | 4)}
							/>
						))}
					</div>
				</Form.Group>
			</Form.Row>

			{/* FCN 27 - Opção 1 */}
			{opcaoIsolamentoSelecionadaFCN === 1 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<p><small>Isolamento espacial (mínimo 200 metros). Isolamento contendo bordadura com, pelo menos, 10 (dez) linhas ou 7 (sete) metros de milho não GM (ou GM aprovado) ao redor das parcelas, mantendo distância mínima de 200 metros de outros plantios.</small></p>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Isolamento adotado/mantida LPMA (metros)</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao1DistanciaMetros" placeholder="Digite a distância em metros" />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Linhas</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao1BordaduraLinhas" placeholder="Número de linhas" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Metros</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao1BordaduraMetros" placeholder="Distância em metros" />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Variedade(s) plantada bordadura</Form.Label>
							<Form.Control ref={registerPlantio} name="fcnOpcao1VariedadeBordadura" placeholder="Digite a variedade" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao1VariedadeBordaduraGM" id="fcnOpcao1GM" value="true" label="GM" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao1VariedadeBordaduraGM" id="fcnOpcao1NaoGM" value="false" label="Não GM" />
						</Form.Group>
					</Form.Row>
				</>
			)}

			{/* FCN 27 - Opção 2 */}
			{opcaoIsolamentoSelecionadaFCN === 2 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<p><small>Isolamento temporal (mínimo 40 dias) com bordadura (20 linhas ou 14 metros). Respeitar período mínimo de 40 dias entre florescimentos.</small></p>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Data plantio da LPMA</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao2DataPlantioLPMA" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Data florescimento LPMA</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao2DataFlorescimentoLPMA" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Data plantio/milho adjacências</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao2DataPlantioAdjacencias" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Data florescimento milho/adjacências</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao2DataFlorescimentoAdjacencias" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Intervalo de florescimento (dias)</Form.Label>
							<Form.Control type="number" ref={registerPlantio} name="fcnOpcao2IntervaloFlorescimentoDias" placeholder="Número de dias" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Label>Bordadura - Linhas</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao2BordaduraLinhas" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Label>Bordadura - Metros</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao2BordaduraMetros" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Variedade(s) plantada bordadura</Form.Label>
							<Form.Control ref={registerPlantio} name="fcnOpcao2VariedadeBordadura" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao2VariedadeBordaduraGM" id="fcnOpcao2GM" value="true" label="GM" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao2VariedadeBordaduraGM" id="fcnOpcao2NaoGM" value="false" label="Não GM" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<small className="text-muted">Atenção: se a bordadura for milho GM aprovado comercialmente, observar 40 dias entre florescimentos com plantios de milho não GM.</small>
						</Form.Group>
					</Form.Row>
				</>
			)}

			{/* FCN 27 - Opção 3 */}
			{opcaoIsolamentoSelecionadaFCN === 3 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<p><small>Isolamento LPMA curto prazo (destruição de parcelas e bordadura até V10). Bordadura mínima de 10 linhas ou 7 metros.</small></p>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Linhas</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao3BordaduraLinhas" />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Metros</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao3BordaduraMetros" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Variedade(s) plantada bordadura</Form.Label>
							<Form.Control ref={registerPlantio} name="fcnOpcao3VariedadeBordadura" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao3VariedadeBordaduraGM" id="fcnOpcao3GM" value="true" label="GM" />
						</Form.Group>
						<Form.Group as={Col} md={3}>
							<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao3VariedadeBordaduraGM" id="fcnOpcao3NaoGM" value="false" label="Não GM" />
						</Form.Group>
					</Form.Row>
				</>
			)}

			{/* FCN 28 - Opção 4 */}
			{opcaoIsolamentoSelecionadaFCN === 4 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<p><small>Isolamento na presença de variedades de milho crioulas (temporal + espacial). Bordadura de 10 linhas ou 7 metros, distância de 200 metros e 40 dias entre florescimentos.</small></p>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Isolamento adotado/mantida LPMA (metros)</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao4DistanciaMetros" placeholder="Distância em metros" />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Linhas</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao4BordaduraLinhas" placeholder="Número de linhas" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Bordadura - Metros</Form.Label>
							<Form.Control type="number" step="0.01" ref={registerPlantio} name="fcnOpcao4BordaduraMetros" placeholder="Distância em metros" />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Variedade(s) plantada bordadura</Form.Label>
							<Form.Control ref={registerPlantio} name="fcnOpcao4VariedadeBordadura" />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<div className="d-flex">
								<div className="form-check mr-4">
									<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao4VariedadeBordaduraGM" id="fcnOpcao4GM" value="true" label="GM" />
								</div>
								<div className="form-check">
									<Form.Check type="radio" ref={registerPlantio} name="fcnOpcao4VariedadeBordaduraGM" id="fcnOpcao4NaoGM" value="false" label="Não GM" />
								</div>
							</div>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Data plantio da LPMA</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao4DataPlantioLPMA" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Data florescimento LPMA</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao4DataFlorescimentoLPMA" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Data plantio/variedade milho crioula nas adjacências</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao4DataPlantioAdjacencias" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Data florescimento/variedade milho crioula nas adjacências</Form.Label>
							<br />
							<Controller control={controlPlantio} name="fcnOpcao4DataFlorescimentoAdjacencias" render={({ onChange, value }) => (
								<DatePicker selected={value} onChange={onChange} className="input-custom-to-date" locale="pt-BR" dateFormat="dd/MM/yyyy" placeholderText="Selecionar a data" />
							)} />
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Intervalo de florescimento (dias)</Form.Label>
							<Form.Control type="number" ref={registerPlantio} name="fcnOpcao4IntervaloFlorescimentoDias" placeholder="Número de dias" />
						</Form.Group>
					</Form.Row>
				</>
			)}

			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<small className="text-muted">Em caso de dúvidas consulte sempre a CIBio BASF – cibio@basf.com</small>
				</Form.Group>
			</Form.Row>

			<hr className="my-4" />

			{/* ===== FCN 30 - FORMULÁRIO - REGISTRO DE ATIVIDADES/INFORMAÇÕES DO PLANTIO - 2 ===== */}
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Form.Label><h2>Formulário - Registro de atividades/informações do Plantio - 2
					</h2></Form.Label>
					<p>Este formulário deve ser preenchido por ocasião do plantio da LPMA.</p>
				</Form.Group>
			</Form.Row>

			{/* Seleção de Abas FCN 30 */}
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
						{abasFcn30Opcoes.map((aba) => (
							<Button
								key={aba.valor}
								title={aba.label}
								contained={abaAtividadesFcn30 === aba.valor}
								className={abaAtividadesFcn30 === aba.valor ? 'active' : ''}
								type="button"
								onClick={() => handleMudarAbaFcn30(aba.valor as 1 | 2 | 3 | 4)}
							/>
						))}
					</div>
				</Form.Group>
			</Form.Row>

			<hr className="my-3" />

			{/* ABA 1 - COLABORADORES */}
			{abaAtividadesFcn30 === 1 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Data inicial/plantio</Form.Label>
							<br />
							<Controller
								control={controlPlantio}
								name="fcn30DataInicialPlantio"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Data final/plantio</Form.Label>
							<br />
							<Controller
								control={controlPlantio}
								name="fcn30DataFinalPlantio"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>
								<strong>Assinatura dos Colaboradores</strong>
								{assinaturaFcn30Colaboradores && (
									<i className="fas fa-check-circle" style={{ 
										color: '#28a745', 
										marginLeft: '10px',
										fontSize: '18px'
									}}></i>
								)}
							</Form.Label>
							
							{!mostrarAssinaturaFcn30 ? (
								<div 
									onClick={() => setMostrarAssinaturaFcn30(true)}
									style={{
										border: assinaturaFcn30Colaboradores ? '2px solid #28a745' : '2px dashed #6c757d',
										borderRadius: '8px',
										padding: '30px 20px',
										textAlign: 'center',
										cursor: 'pointer',
										backgroundColor: assinaturaFcn30Colaboradores ? '#f0f9f4' : '#f8f9fa',
										transition: 'all 0.3s',
										position: 'relative',
										overflow: 'hidden'
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = assinaturaFcn30Colaboradores ? '#e6f4ea' : '#e9ecef';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = assinaturaFcn30Colaboradores ? '#f0f9f4' : '#f8f9fa';
									}}
								>
									{assinaturaFcn30Colaboradores ? (
										<>
											<div style={{
												filter: 'blur(3px)',
												opacity: 0.6,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												minHeight: '60px'
											}}>
												<img 
													src={assinaturaFcn30Colaboradores} 
													alt="Assinatura" 
													style={{ 
														maxWidth: '200px',
														maxHeight: '60px',
														height: 'auto'
													}}
												/>
											</div>
											<div style={{ 
												position: 'absolute',
												top: '50%',
												left: '50%',
												transform: 'translate(-50%, -50%)',
												color: '#28a745',
												fontWeight: 600,
												fontSize: '14px',
												backgroundColor: 'rgba(255, 255, 255, 0.95)',
												padding: '5px 15px',
												borderRadius: '20px',
												border: '1px solid #28a745'
											}}>
												✓ Assinado - Clique para visualizar ou alterar
											</div>
										</>
									) : (
										<>
											<i className="fas fa-signature" style={{ 
												fontSize: '24px', 
												color: '#6c757d', 
												marginBottom: '10px',
												display: 'block'
											}}></i>
											<div style={{ color: '#6c757d', fontWeight: 500 }}>
												Clique aqui para assinar
											</div>
											<small className="text-muted d-block mt-2">
												Sua assinatura cadastrada será aplicada
											</small>
										</>
									)}
								</div>
							) : (
								<Card style={{ marginTop: '10px' }}>
									<Card.Body>
										{assinaturaUsuario ? (
											<>
												<div style={{ textAlign: 'center', marginBottom: '15px' }}>
													<strong>Sua Assinatura:</strong>
												</div>
												<div style={{ 
													display: 'flex', 
													justifyContent: 'center',
													padding: '20px',
													backgroundColor: '#f8f9fa',
													borderRadius: '4px',
													marginBottom: '15px'
												}}>
													<img 
														src={assinaturaUsuario} 
														alt="Sua assinatura" 
														style={{ 
															maxWidth: '300px',
															maxHeight: '100px',
															height: 'auto',
															border: '1px solid #dee2e6',
															borderRadius: '4px',
															padding: '10px',
															backgroundColor: '#fff'
														}}
													/>
												</div>
												<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
													<Button 
														title="Cancelar" 
														contained={false} 
														type="button" 
														onClick={() => setMostrarAssinaturaFcn30(false)}
													/>
													{assinaturaFcn30Colaboradores && (
														<Button 
															title="Remover Assinatura" 
															contained={false} 
															type="button" 
															onClick={() => handleRemoverAssinatura('fcn30')}
														/>
													)}
													<Button 
														title={assinaturaFcn30Colaboradores ? "Alterar Assinatura" : "Confirmar Assinatura"}
														contained 
														type="button" 
														onClick={() => handleAssinar('fcn30')}
													/>
												</div>
											</>
										) : (
											<Alert variant="warning">
												Você ainda não possui uma assinatura cadastrada. 
												<br />
												Acesse <strong>Caderno LPMA → Registros → Assinaturas</strong> para cadastrar.
											</Alert>
										)}
									</Card.Body>
								</Card>
							)}
						</Form.Group>
					</Form.Row>
				</>
			)}

			{/* ABA 2 - LOCALIZAÇÃO */}
			{abaAtividadesFcn30 === 2 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={4}>
							<Form.Label>Talhão</Form.Label>
							<Form.Control
								ref={registerPlantio}
								name="fcn30TalhaoPlantio"
								placeholder="Digite o talhão"
							/>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>Tipo de plantio APROVADO</Form.Label>
							<div className="d-flex" style={{ gap: '12px' }}>
								{renderCheckbox('fcn30TipoPlantioAprovadoMecanico', 'Mecânico', { id: 'fcn30TipoPlantioAprovadoMecanico' })}
								{renderCheckbox('fcn30TipoPlantioAprovadoManual', 'Manual', { id: 'fcn30TipoPlantioAprovadoManual' })}
							</div>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>Tipo de plantio REALIZADO</Form.Label>
							<div className="d-flex" style={{ gap: '12px' }}>
								{renderCheckbox('fcn30TipoPlantioRealizadoMecanico', 'Mecânico', { id: 'fcn30TipoPlantioRealizadoMecanico' })}
								{renderCheckbox('fcn30TipoPlantioRealizadoManual', 'Manual', { id: 'fcn30TipoPlantioRealizadoManual' })}
							</div>
						</Form.Group>
					</Form.Row>

					<hr className="my-3" />

					{/* Resumo/Total geral LPMA */}
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Resumo/Total geral LPMA (m²)</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>OGM Aprovado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30OgmAprovadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>OGM Plantado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30OgmPlantadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Comercial/WT Aprovado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30ComercialWtAprovadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Comercial/WT Plantado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30ComercialWtPlantadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>OGM + Comercial/WT Aprovado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30OgmComercialWtAprovadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>OGM + Comercial/WT Plantado (m²)</Form.Label>
							<Form.Control
								type="number"
								step="0.01"
								ref={registerPlantio}
								name="fcn30OgmComercialWtPlantadoM2"
								placeholder="Digite o valor"
							/>
						</Form.Group>
					</Form.Row>
				</>
			)}

			{/* ABA 3 - COORDENADAS */}
			{abaAtividadesFcn30 === 3 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<small className="text-muted">Formato: graus, minutos e segundos – Ex.: P1: 15° 31' 10.11" - W 54° 23' 54.17"</small>
						</Form.Group>
					</Form.Row>

					{COORDENADAS_FCN30_ROWS.map((pair) => (
						<Form.Row className="row-custom" key={pair[0].name}>
							<CoordenadaDmsField
								control={controlPlantio}
								name={pair[0].name as string}
								label={pair[0].label}
							/>
							<CoordenadaDmsField
								control={controlPlantio}
								name={pair[1].name as string}
								label={pair[1].label}
							/>
						</Form.Row>
					))}
				</>
			)}

			{/* ABA 4 - LIMPEZA/COMUNICAÇÃO */}
			{abaAtividadesFcn30 === 4 && (
				<>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Formulário de limpeza de maquinários/equipamentos</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<div className="table-responsive">
								<table className="table table-bordered table-sm">
									<thead className="table-light">
										<tr>
											<th>Nome e código do maquinário/equipamento utilizado</th>
											<th colSpan={2}>Datas de limpeza</th>
											<th>Método de limpeza</th>
											<th>Responsável</th>
										</tr>
										<tr>
											<th></th>
											<th>Antes ENTRADA</th>
											<th>Antes SAÍDA</th>
											<th></th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{[...Array(quantidadeLinhasLimpezaFcn30)].map((_, index) => (
											<tr key={index}>
												<td>
													<Form.Control
														type="text"
														size="sm"
														placeholder="Nome e código"
														ref={registerPlantio}
														name={`fcn30LimpezaMaquinarioNome${index}`}
													/>
												</td>
												<td>
													<Controller
														control={controlPlantio}
														name={`fcn30LimpezaMaquinarioDataEntrada${index}`}
														render={({ onChange, value }) => (
															<DatePicker
																selected={value}
																onChange={onChange}
																className="input-custom-to-date"
																locale="pt-BR"
																dateFormat="dd/MM/yyyy"
																placeholderText="/ /"
																inline={false}
															/>
														)}
													/>
												</td>
												<td>
													<Controller
														control={controlPlantio}
														name={`fcn30LimpezaMaquinarioDataSaida${index}`}
														render={({ onChange, value }) => (
															<DatePicker
																selected={value}
																onChange={onChange}
																className="input-custom-to-date"
																locale="pt-BR"
																dateFormat="dd/MM/yyyy"
																placeholderText="/ /"
																inline={false}
															/>
														)}
													/>
												</td>
												<td>
													<Form.Control
														type="text"
														size="sm"
														placeholder="Método"
														ref={registerPlantio}
														name={`fcn30LimpezaMaquinarioMetodo${index}`}
													/>
												</td>
												<td>
													<Form.Control
														type="text"
														size="sm"
														placeholder="Responsável"
														ref={registerPlantio}
														name={`fcn30LimpezaMaquinarioResponsavel${index}`}
													/>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Button
								title="Adicionar Equipamento"
								contained={true}
								type="button"
								onClick={() => setQuantidadeLinhasLimpezaFcn30(quantidadeLinhasLimpezaFcn30 + 1)}
							/>
						</Form.Group>
					</Form.Row>

					<hr className="my-3" />

					{/* Comunicação do plantio */}
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Comunicação do plantio</strong></Form.Label>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Comunicação à CIBio sobre o início do plantio – Data:</Form.Label>
							<br />
							<Controller
								control={controlPlantio}
								name="fcn30DataComunicacaoInicioPlantio"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Comunicação à CIBio sobre o término do plantio – Data:</Form.Label>
							<br />
							<Controller
								control={controlPlantio}
								name="fcn30DataComunicacaoTerminoPlantio"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Form.Row>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<p><small className="text-muted"><strong>IMPORTANTE:</strong> Anexar a cópia do (s) e-mail (s) informando a CIBio sobre o início/término do plantio na pasta de campo ABA COMUNICADOS INTERNOS, assim como a cópia do (s) ofício (s) da CIBio para CTNBio/MAPA com as mesmas informações na pasta de campo ABA COMUNICADOS EXTERNOS.</small></p>
						</Form.Group>
					</Form.Row>

					<hr className="my-3" />

					{/* Observações e informações adicionais */}
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>Observações e informações adicionais</strong></Form.Label>
							<Form.Control
								as="textarea"
								rows={4}
								ref={registerPlantio}
								name="fcn30ObservacoesInformacoesAdicionais"
								placeholder="Digite observações e informações adicionais"
							/>
						</Form.Group>
					</Form.Row>
				</>
			)}

	{/* ===== CHECKLIST - PLANTIO 2 ===== */}
	<Form.Row className="row-custom mt-4">
		<Form.Group as={Col} md={12}>
			<Form.Label>
				<h2>Checklist - Plantio 2</h2>
			</Form.Label>
			<p>As perguntas abaixo são pontos de checagem visando o atendimento dos requisitos de biossegurança:</p>
		</Form.Group>
	</Form.Row>

	{/* Item a */}
	<Form.Row className="row-custom mt-3">
		<Form.Group as={Col} md={12}>
			{renderCheckbox(
				'checklistPlantio2PlacaBiossegurancaPreenchia',
				'a. A placa de biossegurança foi preenchida e instalada nas quadras/talhões ocupados pela LPMA?',
			)}
		</Form.Group>
	</Form.Row>
	<Form.Row className="row-custom">
		<Form.Group as={Col} md={12}>
			<Form.Label>Se pertinente comentar:</Form.Label>
			<Form.Control
				as="textarea"
				rows={2}
				ref={registerPlantio}
				name="observacaoChecklistPlantio2PlacaBiossegurancaPreenchia"
				placeholder="Comentários"
			/>
		</Form.Group>
	</Form.Row>

	{/* Item b */}
	<Form.Row className="row-custom mt-3">
		<Form.Group as={Col} md={12}>
			{renderCheckbox(
				'checklistPlantio2ComunicadoPlantio',
				'b. As estacas de delimitação foram colocadas nas extremidades da LPMA?',
			)}
		</Form.Group>
	</Form.Row>
	<Form.Row className="row-custom">
		<Form.Group as={Col} md={12}>
			<Form.Label>Se pertinente comentar:</Form.Label>
			<Form.Control
				as="textarea"
				rows={2}
				ref={registerPlantio}
				name="observacaoChecklistPlantio2ComunicadoPlantio"
				placeholder="Comentários"
			/>
		</Form.Group>
	</Form.Row>

	{/* Item c */}
	<Form.Row className="row-custom mt-3">
		<Form.Group as={Col} md={12}>
			{renderCheckbox(
				'checklistPlantio2ComunicadoPlantioComCroqui',
				'c. O comunicado de plantio com o croqui, mapa e coordenadas geográficas atualizadas foi encaminhado para CIBio?',
			)}
		</Form.Group>
	</Form.Row>
	<Form.Row className="row-custom">
		<Form.Group as={Col} md={12}>
			<Form.Label>Se pertinente comentar:</Form.Label>
			<Form.Control
				as="textarea"
				rows={2}
				ref={registerPlantio}
				name="observacaoChecklistPlantio2ComunicadoPlantioComCroqui"
				placeholder="Comentários"
			/>
		</Form.Group>
	</Form.Row>

	{/* Item d */}
	<Form.Row className="row-custom mt-3">
		<Form.Group as={Col} md={12}>
			{renderCheckbox(
				'checklistPlantio2EnvelopesDestruidos',
				'd. Os envelopes de sementes e embalagens remanescentes do plantio da LPMA foram devidamente destruídos/descartados, e o registro foi realizado na página de destruição e descarte?',
			)}
		</Form.Group>
	</Form.Row>
	<Form.Row className="row-custom">
		<Form.Group as={Col} md={12}>
			<Form.Label>Se pertinente comentar:</Form.Label>
			<Form.Control
				as="textarea"
				rows={2}
				ref={registerPlantio}
				name="observacaoChecklistPlantio2EnvelopesDestruidos"
				placeholder="Comentários"
			/>
		</Form.Group>
	</Form.Row>

	{/* Item e */}
	<Form.Row className="row-custom mt-3">
		<Form.Group as={Col} md={12}>
			{renderCheckbox(
				'checklistPlantio2RoupasEquipamentosLimpos',
				'e. Roupas, calçados assim como maquinários, equipamentos e utensílios utilizados pela equipe foram limpos e inspecionados ANTES de entrar e ANTES de sair da área da LPMA?',
			)}
		</Form.Group>
	</Form.Row>
	<Form.Row className="row-custom">
		<Form.Group as={Col} md={12}>
			<Form.Label>Se pertinente comentar:</Form.Label>
			<Form.Control
				as="textarea"
				rows={2}
				ref={registerPlantio}
				name="observacaoChecklistPlantio2RoupasEquipamentosLimpos"
				placeholder="Comentários"
			/>
		</Form.Group>
	</Form.Row>

			<div className="button-position">
					<Button
						title="Cancelar"
						contained={false}
						type="button"
						onClick={onHide}
					/>
					<Button
						title="Inserir"
						contained={true}
						type="submit"
					/>
				</div>
			</Form>
		</Modal>
	);
}
